'use client';

import { useEffect } from 'react';

import { motion, useReducedMotion } from 'motion/react';

import { AnimatedBot } from '@/components/animated-bot';
import { DEMO_COPY } from '@/lib/content';
import { LINK_STYLE } from '@/lib/utils';

/** Long enough to read the line and see the mark settle, short enough not to feel like a wait. */
const HANDOFF_MS = 900;

/**
 * The visible half of the `/demo` hop.
 *
 * It replaces rather than pushes, so Back returns to the page the visitor came from instead of
 * bouncing them straight back out to the calendar.
 */
export function DemoHandoff({ href }: { href: string }) {
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const go = setTimeout(() => window.location.replace(href), HANDOFF_MS);
    return () => clearTimeout(go);
  }, [href]);

  return (
    <div className="flex min-h-[60svh] flex-col items-center justify-center gap-6 text-center">
      {/* No plate behind it: the mark is the whole illustration here, and a bordered tile around
          it read as an icon slot waiting for something else. */}
      <motion.span
        aria-hidden="true"
        className="text-brand"
        initial={reducedMotion ? false : { opacity: 0, scale: 0.9 }}
        animate={reducedMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: [0, -6, 0] }}
        transition={{
          opacity: { duration: 0.3 },
          scale: { duration: 0.3 },
          y: { duration: 1.6, repeat: Infinity, ease: 'easeInOut' },
        }}
      >
        <AnimatedBot className="size-24" />
      </motion.span>
      <div>
        <p className="text-lg font-medium">{DEMO_COPY.handoff}</p>
        <p className="mt-2 text-base text-muted-foreground">
          {DEMO_COPY.handoffFallback}{' '}
          <a href={href} className={LINK_STYLE}>
            {DEMO_COPY.handoffLink}
          </a>
          .
        </p>
      </div>
    </div>
  );
}
